import { Alert, Heading } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import chatbotCRUDActions from '@mm/state/actions/ChatbotCRUDActions';
import pageCRUDActions from '@mm/state/actions/PageCRUDActions';
import { startBotCRUDActions } from '@mm/state/actions/StartBotCrudActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  combinedFeatureFlagsSelector,
  nextBillingTypeForFeatureSelector,
} from '@mm/state/selectors/featureFlagsSelectors';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { PlanBadge } from '../../../../../../../common/components/ProBadge';
import Switch from '../../../../../../../common/components/Switch/Switch';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../../../../common/widgets/common/PathGraph/PathGraph';
import { NeedHelpLabel } from '../../../../../common/components/NeedHelpLabel/NeedHelpLabel';
import { StartBotsDialogueEditor } from '../../../../../startBotPromoters/common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import './AwaySettings.pcss';

type Props = {
  className?: string;
};

export const AwaySetings = ({ className }: Props) => {
  const widgetCRUDActions = useWidgetCRUDActions();
  const dispatch: Dispatch<StoreState> = useDispatch();
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const session = useSelector((state: StoreState) => state.session.session);

  const activeChatbot = useSelector(
    (state: StoreState) => state.chatbot.activeChatbot,
  );
  const useAwayResponder =
    !!activeFacebookPage && !!activeChatbot?.useAwayResponder;

  const toggleUseAwayResponder = async () => {
    if (!activeChatbot) {
      return;
    }
    if (isUpgradeNeeded) {
      return showUpgradeModal();
    }

    dispatch(
      chatbotCRUDActions.update({
        id: activeChatbot.id,
        useAwayResponder: !useAwayResponder,
      }),
    );
    if (!useAwayResponder && !awayResponderPage) {
      await dispatch(
        startBotCRUDActions.create(activeChatbot.id, 'away_responder'),
      );

      dispatch(pageCRUDActions.retrieveAll(activeChatbot.id));
    }
  };

  const awayResponderPage = useSelector((state: StoreState) =>
    state.page.entities.find(p => p.kind === 'away_responder'),
  );

  const pathGraph = useNewGraphInstance(awayResponderPage?.id);

  const widgets = useSelector((state: StoreState) =>
    awayResponderPage
      ? widgetsByDialogueSelector(awayResponderPage.id, state.widget.entities)
      : [],
  );
  const { openUniversalUpgradeLandmineModal } = useModalActions();

  const billingType = useSelector((state: StoreState) =>
    nextBillingTypeForFeatureSelector(state, 'awaySetting'),
  );
  const isUpgradeNeeded = !useSelector(combinedFeatureFlagsSelector)
    .awaySetting;

  const [alertVisible, setAlertVisible] = useState(
    isUpgradeNeeded && useAwayResponder,
  );

  const onCloseAlert = () => {
    setAlertVisible(false);
  };

  const showUpgradeModal = () => {
    openUniversalUpgradeLandmineModal({
      landmineName: landmineNames['BUSINESS-HOURS-AWAY-SETTINGS'],
      feature: 'awaySetting',
    });
  };

  useEffect(() => {
    if (awayResponderPage) {
      widgetCRUDActions.retrieveAll(awayResponderPage.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awayResponderPage?.id]);

  if (!activeChatbot) {
    return null;
  }

  const testStartbot = () => {
    if (activeFacebookPage && awayResponderPage) {
      window.open(
        `http://m.me/${activeFacebookPage.remoteId}?ref=${awayResponderPage.searchToken}_${session.searchToken}`,
      );
    }
  };

  const labelClassname = classNames(
    'away-settings__use-away-responder-control',
    {
      'away-settings__use-away-responder-control--disabled': isUpgradeNeeded,
    },
  );

  return (
    <div className={classNames('away-settings', className)}>
      <div className="away-settings__heading">
        <Heading size={4} text="Away Settings" />
        {billingType && (
          <PlanBadge
            className="away-settings__pro_badge"
            billingType={billingType}
          />
        )}
      </div>
      <label className={labelClassname}>
        <Switch
          checked={useAwayResponder}
          onChange={toggleUseAwayResponder}
          className="away-settings__use-away-responder-switch"
        />
        <div className="away-settings__use-away-responder-label">
          Use a different Dialogue during off hours for messages sent directly
          to my FB page &amp; customer chat widget
          <NeedHelpLabel
            link="https://mobilemonkey.com/help/article/5fxe0bh3gm"
            iconOnly
          />
        </div>
      </label>
      {alertVisible ? (
        <Alert
          type="warning"
          className="away-settings__warning-alert"
          closeIconProps={{ onClick: onCloseAlert }}
        >
          You no longer have access to this feature on your free plan. Please{' '}
          <span
            className="away-settings__warning-alert-upgrate-text"
            onClick={showUpgradeModal}
          >
            upgrade your plan.
          </span>
        </Alert>
      ) : (
        <>
          {useAwayResponder && awayResponderPage && !isUpgradeNeeded && (
            <>
              <PathGraphContext.Provider value={pathGraph}>
                <StartBotsDialogueEditor
                  widgets={widgets}
                  dialogueId={awayResponderPage.id}
                  chatbotId={activeChatbot.id}
                  platforms={['facebook']}
                />
              </PathGraphContext.Provider>
              <PrimaryButton
                className="away-settings__test-startbot-button"
                onClick={testStartbot}
              >
                Test Dialogue
              </PrimaryButton>
            </>
          )}
        </>
      )}
    </div>
  );
};
